"use strict";
/**
 * Hook do ładowania szablonu Excel z API przy starcie aplikacji
 *
 * Funkcjonalność:
 * - Ładuje szablon tylko raz per sesja (uruchomienie dodatku)
 * - Śledzi stan ładowania (loading, error, loaded)
 * - Używa sessionStorage do zapobiegania wielokrotnemu ładowaniu
 *
 * Uwaga: Consuming application musi dostarczyć funkcję loadTemplateFn,
 * która pobiera dane szablonu z API i wywołuje createSheetFromData()
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTemplateLoader = void 0;
var react_1 = require("react");
/**
 * Domyślny klucz w sessionStorage
 */
var DEFAULT_SESSION_KEY = 'excel_template_loaded_session';
/**
 * Hook do zarządzania procesem ładowania szablonu Excel
 *
 * @param options - Konfiguracja hooka
 * @returns Stan i funkcje do zarządzania ładowaniem
 *
 * @example
 * ```typescript
 * import { useTemplateLoader } from '@carbowaste/excel-template-loader';
 * import { CarboWasteApi } from './api/CarboWasteApi';
 *
 * function MyComponent() {
 *   const { isLoading, error, isLoaded, loadTemplate } = useTemplateLoader({
 *     loadTemplateFn: async () => {
 *       await CarboWasteApi.loadTemplateFromAPI();
 *     }
 *   });
 *
 *   useEffect(() => {
 *     loadTemplate();
 *   }, [loadTemplate]);
 *
 *   if (isLoading) return <div>Loading template...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   if (isLoaded) return <div>Template ready!</div>;
 * }
 * ```
 */
var useTemplateLoader = function (options) {
    var loadTemplateFn = options.loadTemplateFn, _a = options.sessionStorageKey, sessionStorageKey = _a === void 0 ? DEFAULT_SESSION_KEY : _a;
    var _b = (0, react_1.useState)(false), isLoading = _b[0], setIsLoading = _b[1];
    var _c = (0, react_1.useState)(null), error = _c[0], setError = _c[1];
    var _d = (0, react_1.useState)(false), isLoaded = _d[0], setIsLoaded = _d[1];
    // Użyj ref do śledzenia czy ładowanie jest w toku (bardziej niezawodne niż state)
    var isLoadingRef = (0, react_1.useRef)(false);
    var loadTemplate = (0, react_1.useCallback)(function () { return __awaiter(void 0, void 0, void 0, function () {
        var wasLoadedInSession, err_1, errorMessage;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    console.log('🔍 [useTemplateLoader] loadTemplate called', {
                        isLoading: isLoadingRef.current,
                        isLoaded: isLoaded,
                        sessionStorage: sessionStorage.getItem(sessionStorageKey)
                    });
                    wasLoadedInSession = sessionStorage.getItem(sessionStorageKey);
                    if (wasLoadedInSession === 'true') {
                        console.log('📋 Template already loaded in this session, skipping...');
                        setIsLoaded(true);
                        return [2 /*return*/];
                    }
                    // Jeśli szablon już załadowany w tym komponencie, nie ładuj ponownie
                    if (isLoaded) {
                        console.log('📋 Template already loaded, skipping...');
                        return [2 /*return*/];
                    }
                    // Jeśli trwa ładowanie, nie uruchamiaj ponownie (używamy ref dla natychmiastowej synchroniczności)
                    if (isLoadingRef.current) {
                        console.log('⏳ Template loading already in progress (ref check), skipping...');
                        return [2 /*return*/];
                    }
                    console.log('📥 Starting template load from API...');
                    isLoadingRef.current = true;
                    setIsLoading(true);
                    setError(null);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    // Wywołaj funkcję ładującą dostarczoną przez consuming app
                    console.log('🔧 Calling loadTemplateFn()...');
                    return [4 /*yield*/, loadTemplateFn()];
                case 2:
                    _a.sent();
                    console.log('✅ Template loaded successfully');
                    // Zapisz w sessionStorage że szablon został załadowany w tej sesji
                    sessionStorage.setItem(sessionStorageKey, 'true');
                    console.log('💾 Saved template loaded flag to sessionStorage');
                    setIsLoaded(true);
                    setError(null);
                    return [3 /*break*/, 5];
                case 3:
                    err_1 = _a.sent();
                    errorMessage = err_1 instanceof Error ? err_1.message : 'Unknown error loading template';
                    console.error('❌ Template loading failed:', errorMessage, err_1);
                    setError(errorMessage);
                    setIsLoaded(false);
                    return [3 /*break*/, 5];
                case 4:
                    isLoadingRef.current = false;
                    setIsLoading(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [isLoaded, loadTemplateFn, sessionStorageKey]); // Dodaj loadTemplateFn do dependencies
    return {
        isLoading: isLoading,
        error: error,
        isLoaded: isLoaded,
        loadTemplate: loadTemplate
    };
};
exports.useTemplateLoader = useTemplateLoader;
//# sourceMappingURL=useTemplateLoader.js.map